import { CommonControlsHierarchyResponse } from '../types/dynamodb';
import type { DataService } from './dataServiceFactory';

const STATIC_METADATA_URL = '/metadata/common-controls-hierarchy.v1.json';
const SESSION_CACHE_KEY = 'nexus.commonControlsHierarchy.v1';
const SESSION_CACHE_TTL_MS = 12 * 60 * 60 * 1000;

let inMemoryCache: CommonControlsHierarchyResponse | null = null;
let inFlightRequest: Promise<CommonControlsHierarchyResponse> | null = null;

type StaticMetadataPayload = {
  domains?: unknown;
  objectives?: unknown;
  controls?: unknown;
};

type SessionCachePayload = {
  cachedAt: number;
  data: CommonControlsHierarchyResponse;
};

const isString = (value: unknown): value is string => typeof value === 'string';

const isHierarchyShape = (value: unknown): value is CommonControlsHierarchyResponse => {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const payload = value as StaticMetadataPayload;
  if (!Array.isArray(payload.domains) || !Array.isArray(payload.objectives) || !Array.isArray(payload.controls)) {
    return false;
  }

  const hasValidDomain = payload.domains.every(
    (domain: any) =>
      domain &&
      isString(domain.code) &&
      isString(domain.title) &&
      isString(domain.description)
  );
  if (!hasValidDomain) {
    return false;
  }

  const hasValidObjective = payload.objectives.every(
    (objective: any) =>
      objective &&
      isString(objective.code) &&
      isString(objective.title) &&
      isString(objective.description) &&
      isString(objective.domainCode)
  );
  if (!hasValidObjective) {
    return false;
  }

  const hasValidControl = payload.controls.every(
    (control: any) =>
      control &&
      isString(control.code) &&
      isString(control.title) &&
      isString(control.description) &&
      isString(control.objectiveCode) &&
      isString(control.domainCode) &&
      isString(control.controlKey)
  );
  return hasValidControl;
};

const readFromSessionCache = (): CommonControlsHierarchyResponse | null => {
  try {
    const raw = sessionStorage.getItem(SESSION_CACHE_KEY);
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw) as SessionCachePayload;
    if (
      !parsed ||
      typeof parsed.cachedAt !== 'number' ||
      Date.now() - parsed.cachedAt > SESSION_CACHE_TTL_MS
    ) {
      sessionStorage.removeItem(SESSION_CACHE_KEY);
      return null;
    }

    if (!isHierarchyShape(parsed.data)) {
      sessionStorage.removeItem(SESSION_CACHE_KEY);
      return null;
    }

    return parsed.data;
  } catch {
    return null;
  }
};

const writeToSessionCache = (data: CommonControlsHierarchyResponse): void => {
  try {
    const payload: SessionCachePayload = {
      cachedAt: Date.now(),
      data,
    };
    sessionStorage.setItem(SESSION_CACHE_KEY, JSON.stringify(payload));
  } catch {
    // Best effort cache only.
  }
};

const loadFromStaticMetadata = async (): Promise<CommonControlsHierarchyResponse> => {
  const response = await fetch(STATIC_METADATA_URL, {
    method: 'GET',
    cache: 'force-cache',
    credentials: 'same-origin',
  });

  if (!response.ok) {
    throw new Error(`Static metadata fetch failed with status ${response.status}`);
  }

  const payload = (await response.json()) as StaticMetadataPayload;
  if (!isHierarchyShape(payload)) {
    throw new Error('Static metadata payload shape is invalid');
  }

  return {
    domains: payload.domains as CommonControlsHierarchyResponse['domains'],
    objectives: payload.objectives as CommonControlsHierarchyResponse['objectives'],
    controls: payload.controls as CommonControlsHierarchyResponse['controls'],
  };
};

export const loadCommonControlsHierarchy = async (
  dataService: Pick<DataService, 'getCommonControlsWithHierarchy'>
): Promise<CommonControlsHierarchyResponse> => {
  if (inMemoryCache) {
    return inMemoryCache;
  }

  const sessionData = readFromSessionCache();
  if (sessionData) {
    inMemoryCache = sessionData;
    return sessionData;
  }

  if (!inFlightRequest) {
    inFlightRequest = (async () => {
      try {
        const staticData = await loadFromStaticMetadata();
        inMemoryCache = staticData;
        writeToSessionCache(staticData);
        return staticData;
      } catch (error) {
        console.warn('Static Common-Controls metadata unavailable, falling back to API:', error);
        const apiData = await dataService.getCommonControlsWithHierarchy();
        inMemoryCache = apiData;
        writeToSessionCache(apiData);
        return apiData;
      }
    })().finally(() => {
      inFlightRequest = null;
    });
  }

  return inFlightRequest;
};
